/**
 * The estimate_value behavior's pure core — shared by both runtimes.
 *
 * One copy for the app (src/lib/llmValueEstimatorService.ts,
 * valueEstimatorPrecedent.ts, agentCalibrationService.ts,
 * agentLearnedRulesService.ts, valueEstimatorRunner.ts and the Value
 * Estimator definition re-export from here) and for the ambient agent
 * runtime, which runs the same behavior server-side for scheduled, event and
 * task runs (SPEC-custom-agent-template.md §4: "one runner, two hosts …
 * the same behavior cores").
 *
 * Data loading, the model call and persistence live with each runtime.
 */

import { jaccardSimilarity } from './itemTextCore.ts';
import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { fenceReferenceDocs } from './referenceDocFence.ts';

// ── The behavior's job (canon doc 20) — the prompt-relevant half of its definition ──

export const ESTIMATE_VALUE_SYSTEM_PROMPT =
  'You are the Value Estimator for a prioritization workspace. You propose one value for one ' +
  'attribute of one item, on the scale given, from the evidence given. You cite the evidence you ' +
  'leaned on by its id. When the item is too thin to estimate, you abstain and name what is ' +
  'missing. You never invent precedent. Respond with valid JSON only.';

export const ESTIMATE_VALUE_JOB = {
  capabilityId: 'value_estimator',
  role: 'Estimate one attribute of one item from evidence, for a person to review.',
  goal:
    'Give the item\'s owner a defensible proposed value, with the precedent it rests on, before a ' +
    'session ever asks for it — or a clear statement of what the item is missing.',
  outputContract:
    'Per item and attribute: exactly one value proposal on the scale in effect (value, confidence, ' +
    'rationale naming evidence ids), or one abstain with the missing input named. Nothing else.',
  skillStack: ['Clarity pre-check', 'Precedent retrieval', 'Comparative estimation', 'Rationale compression'],
  /** What the agent may read, stated for the model (the card's read bound). */
  reads: [
    'the item\'s title, description, category and altitude',
    'accepted values on similar items in this workspace',
    'reference documents an administrator added',
    'connected history from linked tools when that source is on',
  ],
  /** What it may propose, stated for the model (the card's propose bound). */
  proposes: ['one value proposal on the given scale, or one abstain naming what is missing'],
  /** The platform bound on the model parameters; an administrator may lower them (§2 H). */
  modelDefaults: { temperature: 0.2, maxTokens: 500 },
} as const;

export function estimateValueCard(): AgentCard {
  return {
    capabilityId: ESTIMATE_VALUE_JOB.capabilityId,
    role: ESTIMATE_VALUE_JOB.role,
    goal: ESTIMATE_VALUE_JOB.goal,
    permissions: { read: [...ESTIMATE_VALUE_JOB.reads], propose: [...ESTIMATE_VALUE_JOB.proposes] },
    memory: 'turn',
    skillStack: [...ESTIMATE_VALUE_JOB.skillStack],
    outputContract: ESTIMATE_VALUE_JOB.outputContract,
  };
}

// ── Model parameters (§2 H) ───────────────────────────────────────────────────

export interface ModelParams {
  temperature: number;
  maxTokens: number;
  /** An output language for prose (rationale, reasons). Null = the platform default (English). */
  language: string | null;
}

export const LANGUAGE_NAMES: Record<string, string> = { en: 'English', fr: 'French' };

/**
 * The parameters a run uses: the stored values, held inside the platform
 * bound — an administrator may lower temperature and length, never raise
 * them past the definition's defaults.
 */
export function resolveModelParams(
  bound: { temperature: number; maxTokens: number },
  stored: Record<string, unknown> | null | undefined,
): ModelParams {
  const t = typeof stored?.temperature === 'number' ? stored.temperature : bound.temperature;
  const m = typeof stored?.maxTokens === 'number' ? stored.maxTokens : bound.maxTokens;
  const lang = typeof stored?.language === 'string' && LANGUAGE_NAMES[stored.language] ? stored.language : null;
  return {
    temperature: Math.max(0, Math.min(bound.temperature, t)),
    maxTokens: Math.max(100, Math.min(bound.maxTokens, Math.round(m))),
    language: lang,
  };
}

/**
 * The system prompt for an estimate run: the full instruction stack (L0 law,
 * L1 charter, L2 card, the behavior's own contract as the appendix) with the
 * workspace's text as the subordinate overlay — it specializes, it never
 * replaces the contract (§2 H). A language line is added when one is set.
 */
export function buildEstimateSystemPrompt(params: {
  overlay?: string | null;
  language?: string | null;
}): { systemPrompt: string; l0Version: string; l1Version: string } {
  const overlay = params.overlay?.trim();
  // A stored overlay equal to the contract is no overlay at all.
  const effectiveOverlay = overlay && overlay !== ESTIMATE_VALUE_SYSTEM_PROMPT ? overlay : null;
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write the rationale and any reason in ${LANGUAGE_NAMES[params.language]}. Keep scale labels and JSON keys exactly as given.`
    : null;
  const { systemPrompt, meta } = buildInstructionStack({
    card: estimateValueCard(),
    capabilityAppendix: [ESTIMATE_VALUE_SYSTEM_PROMPT, language].filter(Boolean).join('\n'),
    workspaceOverlay: effectiveOverlay,
  });
  return { systemPrompt, l0Version: meta.l0Version, l1Version: meta.l1Version };
}

// ── The behavior's own switches (agent_settings.behavior_config) ─────────────

export interface ValueEstimatorBehaviorConfig {
  /** Attribute keys the agent may propose for. Owner ruling: effort first. */
  attributes: string[];
  /** Owner ruling: estimate by default; a per-agent toggle may set score. */
  valueKind: 'estimate' | 'score';
  /** Owner ruling: an accepted effort value may also feed capacity units. */
  target: 'attribute' | 'capacity_units' | 'both';
  /** Stated confidence (0–1) below which the agent abstains. */
  confidenceFloor: number;
  /** Allow a proposal built on the model alone, marked "no precedent found". */
  allowModelOnly: boolean;
  /** Items with a clarity score below this abstain before any model call (0–100). */
  clarityFloor: number;
}

export const DEFAULT_VALUE_ESTIMATOR_CONFIG: ValueEstimatorBehaviorConfig = {
  attributes: ['effort'],
  valueKind: 'estimate',
  target: 'attribute',
  confidenceFloor: 0.5,
  allowModelOnly: true,
  clarityFloor: 60,
};

export function resolveEstimatorConfigFrom(raw: Partial<ValueEstimatorBehaviorConfig> | null | undefined): ValueEstimatorBehaviorConfig {
  const r = raw ?? {};
  return {
    attributes: Array.isArray(r.attributes) && r.attributes.length ? r.attributes : DEFAULT_VALUE_ESTIMATOR_CONFIG.attributes,
    valueKind: r.valueKind === 'score' ? 'score' : 'estimate',
    target: r.target === 'capacity_units' || r.target === 'both' ? r.target : 'attribute',
    confidenceFloor: typeof r.confidenceFloor === 'number' ? r.confidenceFloor : DEFAULT_VALUE_ESTIMATOR_CONFIG.confidenceFloor,
    allowModelOnly: typeof r.allowModelOnly === 'boolean' ? r.allowModelOnly : DEFAULT_VALUE_ESTIMATOR_CONFIG.allowModelOnly,
    clarityFloor: typeof r.clarityFloor === 'number' ? r.clarityFloor : DEFAULT_VALUE_ESTIMATOR_CONFIG.clarityFloor,
  };
}

// ── Evidence shapes ───────────────────────────────────────────────────────────

export interface ConnectedHistoryLine {
  /** The citation id the model may use. */
  id: string;
  summary: string;
  linked: boolean;
  storyPoints: number | null;
  estimateHours: number | null;
  spentHours: number | null;
  cycleDays: number | null;
  status: string | null;
}

export interface EstimatePrecedent {
  itemId: string;
  title: string;
  category?: string | null;
  altitudeLevel?: number | string | null;
  /** The accepted value's label on the same scale. */
  value: string;
  /** 0–1, from the precedent ranker. */
  similarity: number;
}

export interface EstimateReferenceDoc {
  id: string;
  title: string;
  content: string;
}

export interface EstimateScalePoint {
  value: number;
  label: string;
  description?: string;
}

export interface EstimatePromptInput {
  item: {
    id: string;
    title: string;
    description?: string | null;
    category?: string | null;
    altitudeLevel?: number | string | null;
  };
  attribute: { key: string; label: string; definition?: string | null };
  scale: { id: string | null; label: string; type: string; points: EstimateScalePoint[] };
  precedents: EstimatePrecedent[];
  /** Linked and similar issues from the connected system, with their sizing and duration (source 2). */
  connected?: ConnectedHistoryLine[];
  referenceDocs: EstimateReferenceDoc[];
  /** Rules an administrator approved for this workspace (migration 830). Plain sentences. */
  rules?: string[];
  /** When false and no precedent or document exists, the run abstains without a call. */
  allowModelOnly: boolean;
}

export type EstimateConfidence = 'high' | 'medium' | 'low';

export type EstimateResult =
  | {
      kind: 'estimate';
      /** The chosen scale point's label, exactly as given. */
      value: string;
      pointValue: number;
      confidence: EstimateConfidence;
      /** 0–1 mapped from the stated confidence. */
      confidenceScore: number;
      rationale: string;
      /** Precedent item ids and document ids the model says it leaned on. */
      evidenceUsed: string[];
      /** True when no precedent or document was available: a model-only proposal. */
      modelOnly: boolean;
      wouldChangeIf?: string;
    }
  | { kind: 'abstain'; reason: string };

export const CONFIDENCE_SCORES: Record<EstimateConfidence, number> = { high: 0.85, medium: 0.6, low: 0.35 };

/** No precedent, no document, no connected history, and model-only proposals off: abstain before any call. */
export function abstainBeforeCall(input: EstimatePromptInput): EstimateResult | null {
  if (!input.allowModelOnly && input.precedents.length === 0 && input.referenceDocs.length === 0 && !(input.connected?.length)) {
    return { kind: 'abstain', reason: 'No precedent or reference document is available, and model-only proposals are off for this workspace.' };
  }
  return null;
}

// ── Scale ─────────────────────────────────────────────────────────────────────

export interface ResolvedScale {
  scaleId: string | null;
  label: string;
  type: string;
  points: EstimateScalePoint[];
}

/** What scale resolution needs from an attribute (the app's Attribute, or an attributes row). */
export interface ScaledAttribute {
  label: string;
  scales?: Array<{ id: string; label: string; scaleType?: string; isDefault?: boolean; points?: Array<{ value: number; label: string; description?: string }> }>;
  defaultScaleId?: string | null;
  options?: string[] | null;
}

/**
 * The scale the estimate is made on: the attribute's default scale, else its
 * first scale, else its legacy options as an ordinal scale. Null when the
 * attribute has nothing to choose from.
 */
export function resolveScaleFrom(attr: ScaledAttribute): ResolvedScale | null {
  const scales = attr.scales ?? [];
  if (scales.length) {
    const scale = scales.find((s) => s.id === attr.defaultScaleId) ?? scales.find((s) => s.isDefault) ?? scales[0];
    const points = (scale.points ?? []).map((p) => ({ value: p.value, label: p.label, description: p.description }));
    return points.length ? { scaleId: scale.id, label: scale.label, type: scale.scaleType ?? 'ordinal', points } : null;
  }
  if (attr.options?.length) {
    return { scaleId: null, label: attr.label, type: 'ordinal', points: attr.options.map((label, i) => ({ value: i + 1, label })) };
  }
  return null;
}

// ── Connected history shaping ─────────────────────────────────────────────────

/** What the integrations engine's /jira/estimates returns per issue. */
export interface JiraIssueEstimate {
  key: string;
  summary: string;
  status: string | null;
  storyPoints: number | null;
  originalEstimateSeconds: number | null;
  timeSpentSeconds: number | null;
  cycleDays: number | null;
  linked: boolean;
}

function hours(seconds: number | null): number | null {
  return seconds == null ? null : Math.round((seconds / 3600) * 10) / 10;
}

export function toConnectedLine(issue: JiraIssueEstimate): ConnectedHistoryLine {
  return {
    id: issue.key,
    summary: issue.summary,
    linked: issue.linked,
    storyPoints: issue.storyPoints,
    estimateHours: hours(issue.originalEstimateSeconds),
    spentHours: hours(issue.timeSpentSeconds),
    cycleDays: issue.cycleDays,
    status: issue.status,
  };
}

/** A line is evidence only when it says something about size or duration. */
export function hasSizingSignal(line: ConnectedHistoryLine): boolean {
  return line.storyPoints != null || line.estimateHours != null || line.spentHours != null || line.cycleDays != null;
}

// ── Prompt ────────────────────────────────────────────────────────────────────

export function buildEstimatePrompt(input: EstimatePromptInput): string {
  const { item, attribute, scale, precedents, referenceDocs } = input;
  const points = scale.points
    .map((p) => `- "${p.label}" (${p.value})${p.description ? `: ${p.description}` : ''}`)
    .join('\n');
  const precedentLines = precedents.length
    ? precedents
        .map(
          (p) =>
            `- id ${p.itemId} · "${p.title}"${p.category ? ` · ${p.category}` : ''}${
              p.altitudeLevel != null ? ` · altitude ${p.altitudeLevel}` : ''
            } · accepted ${attribute.label}: "${p.value}" · similarity ${p.similarity.toFixed(2)}`,
        )
        .join('\n')
    : '(none found in this workspace)';
  const connectedLines = input.connected?.length
    ? input.connected
        .map((c) => {
          const parts = [
            c.storyPoints != null ? `${c.storyPoints} points` : null,
            c.estimateHours != null ? `estimated ${c.estimateHours}h` : null,
            c.spentHours != null ? `spent ${c.spentHours}h` : null,
            c.cycleDays != null ? `took ${c.cycleDays} days` : null,
            c.status ? c.status : null,
          ].filter(Boolean);
          return `- id ${c.id}${c.linked ? ' (linked to this item)' : ''} · "${c.summary}" · ${parts.join(' · ')}`;
        })
        .join('\n')
    : '(none)';
  const docLines =
    fenceReferenceDocs(referenceDocs, `how ${attribute.label} is sized here`, { heading: '' }) || '(none)';

  return `Estimate ONE attribute for ONE item.

## Item
Title: ${item.title}
Description: ${item.description?.trim() || '(none)'}
Category: ${item.category ?? '(unknown)'}
Altitude: ${item.altitudeLevel ?? '(unknown)'}

## Attribute
${attribute.label} (key: ${attribute.key})
${attribute.definition?.trim() ? `Definition: ${attribute.definition.trim()}` : ''}

## Scale: ${scale.label} (${scale.type})
Choose exactly one of these labels, spelled exactly as written:
${points}

## Evidence, in trust order
### 1. Accepted values on similar items in this workspace
${precedentLines}

### 2. Connected history (the linked issue and issues like this one, with what they took)
${connectedLines}

### 3. Reference documents from the administrator
${docLines}

### 4. Your own judgment
Lowest trust. If 1, 2 and 3 are empty, say so in the rationale.
${
  input.rules?.length
    ? `
## Workspace rules (approved by an administrator — follow them)
${input.rules.map((r, i) => `${i + 1}. ${r}`).join('\n')}
`
    : ''
}
## Rules
- Cite the ids (item ids, doc ids) you leaned on. Never cite an id that is not listed.
- If the title and description do not say what the work is, abstain and name what is missing.
- confidence: "high" only when at least two cited precedents agree; "low" when you lean on your own judgment alone.
- rationale: two sentences, plain words, naming the evidence.

## Output (JSON only)
Either
{ "kind": "estimate", "value": "<one scale label exactly>", "confidence": "<high|medium|low>", "rationale": "<two sentences>", "evidenceUsed": ["<id>", ...], "wouldChangeIf": "<one line, optional>" }
or
{ "kind": "abstain", "reason": "<what is missing, one sentence>" }`;
}

// ── Parse ─────────────────────────────────────────────────────────────────────

function extractJson(raw: string): string {
  const trimmed = raw.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  return start >= 0 && end > start ? trimmed.slice(start, end + 1) : trimmed;
}

/**
 * Parse the model's reply against the input. Anything that is not a listed
 * scale label, or that cites an unlisted id, is corrected rather than trusted:
 * unknown ids are dropped, and a value that matches no label becomes an abstain.
 */
export function parseEstimateResponse(raw: string, input: EstimatePromptInput): EstimateResult {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(extractJson(raw)) as Record<string, unknown>;
  } catch {
    return { kind: 'abstain', reason: 'The model did not return a readable answer.' };
  }

  if (parsed.kind === 'abstain') {
    const reason = typeof parsed.reason === 'string' && parsed.reason.trim() ? parsed.reason.trim() : 'The item is too thin to estimate.';
    return { kind: 'abstain', reason };
  }

  const valueRaw = typeof parsed.value === 'string' ? parsed.value.trim() : String(parsed.value ?? '').trim();
  const point =
    input.scale.points.find((p) => p.label === valueRaw) ??
    input.scale.points.find((p) => p.label.toLowerCase() === valueRaw.toLowerCase()) ??
    input.scale.points.find((p) => String(p.value) === valueRaw);
  if (!point) {
    return { kind: 'abstain', reason: `The model answered "${valueRaw || '(empty)'}", which is not a point on the ${input.scale.label} scale.` };
  }

  const confidence: EstimateConfidence =
    parsed.confidence === 'high' || parsed.confidence === 'low' ? parsed.confidence : 'medium';

  const knownIds = new Set<string>([
    ...input.precedents.map((p) => p.itemId),
    ...(input.connected ?? []).map((c) => c.id),
    ...input.referenceDocs.map((d) => d.id),
  ]);
  const evidenceUsed = (Array.isArray(parsed.evidenceUsed) ? parsed.evidenceUsed : [])
    .filter((id): id is string => typeof id === 'string' && knownIds.has(id));

  const modelOnly = input.precedents.length === 0 && input.referenceDocs.length === 0 && !(input.connected?.length);
  const rationale = typeof parsed.rationale === 'string' && parsed.rationale.trim()
    ? parsed.rationale.trim()
    : 'No rationale given.';

  return {
    kind: 'estimate',
    value: point.label,
    pointValue: point.value,
    // A stated "high" with no cited precedent is not high.
    confidence: confidence === 'high' && evidenceUsed.length < 2 ? 'medium' : confidence,
    confidenceScore: CONFIDENCE_SCORES[confidence === 'high' && evidenceUsed.length < 2 ? 'medium' : confidence],
    rationale: modelOnly ? `No precedent found in this workspace. ${rationale}` : rationale,
    evidenceUsed,
    modelOnly,
    ...(typeof parsed.wouldChangeIf === 'string' && parsed.wouldChangeIf.trim()
      ? { wouldChangeIf: parsed.wouldChangeIf.trim() }
      : {}),
  };
}

// ── Precedent ranking ─────────────────────────────────────────────────────────

export interface PrecedentCandidate {
  itemId: string;
  title: string;
  description?: string | null;
  category?: string | null;
  altitudeLevel?: number | string | null;
  /** The accepted value's label. */
  value: string;
}

export interface PrecedentTarget {
  itemId: string;
  title: string;
  description?: string | null;
  category?: string | null;
  altitudeLevel?: number | string | null;
}

/** Weights: category 0.3, altitude 0.2, text 0.5. */
export function scorePrecedent(target: PrecedentTarget, candidate: PrecedentCandidate): number {
  const categoryMatch = target.category && candidate.category && target.category === candidate.category ? 1 : 0;
  const altitudeMatch =
    target.altitudeLevel != null && candidate.altitudeLevel != null && String(target.altitudeLevel) === String(candidate.altitudeLevel)
      ? 1
      : 0;
  const targetText = `${target.title} ${target.description ?? ''}`;
  const candidateText = `${candidate.title} ${candidate.description ?? ''}`;
  const text = jaccardSimilarity(targetText, candidateText);
  return 0.3 * categoryMatch + 0.2 * altitudeMatch + 0.5 * text;
}

/**
 * Rank candidates for a target and keep the best. A candidate scoring below
 * `floor` is not precedent at all — a model told "here is a similar item" that
 * shares no words with the target would learn the wrong lesson.
 */
export function rankPrecedents(
  target: PrecedentTarget,
  candidates: PrecedentCandidate[],
  limit = 5,
  floor = 0.15,
): EstimatePrecedent[] {
  return candidates
    .filter((c) => c.itemId !== target.itemId)
    .map((c) => ({ c, s: scorePrecedent(target, c) }))
    .filter(({ s }) => s >= floor)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map(({ c, s }) => ({
      itemId: c.itemId,
      title: c.title,
      category: c.category ?? null,
      altitudeLevel: c.altitudeLevel ?? null,
      value: c.value,
      similarity: Math.round(s * 100) / 100,
    }));
}

/** An accepted value as the label a person saw. */
export function valueLabel(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value !== null && 'label' in value) return String((value as { label: unknown }).label);
  return JSON.stringify(value);
}

// ── Calibration ───────────────────────────────────────────────────────────────

export type ConfidenceBucket = 'high' | 'medium' | 'low';

/** What calibration needs from a reliability-table row. */
export interface CalibrationBucketRow {
  field: string;
  bucket: ConfidenceBucket;
  reviewedCount: number;
  expiredCount: number;
  acceptedRate: number | null;
}

/** Below this many reviews a bucket's rate is noise, and the stated confidence stands. */
export const MIN_REVIEWS_FOR_CALIBRATION = 30;

/** Matches the database function confidence_bucket(). */
export function confidenceBucket(score: number | null | undefined): ConfidenceBucket {
  if (score == null) return 'medium';
  if (score >= 0.75) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

/**
 * The confidence to show: the bucket's accepted rate when the bucket has
 * enough reviews, else the stated score. Says which one it was.
 */
export function calibrateConfidence(
  stated: number,
  rows: CalibrationBucketRow[],
  field: string,
  minReviews = MIN_REVIEWS_FOR_CALIBRATION,
): { confidence: number; calibrated: boolean; reviewed: number } {
  const bucket = confidenceBucket(stated);
  const row = rows.find((r) => r.field === field && r.bucket === bucket);
  const effective = (row?.reviewedCount ?? 0) - (row?.expiredCount ?? 0);
  if (row && row.acceptedRate != null && effective >= minReviews) {
    return { confidence: Math.round(row.acceptedRate * 100) / 100, calibrated: true, reviewed: effective };
  }
  return { confidence: stated, calibrated: false, reviewed: effective };
}

// ── Learned rules ─────────────────────────────────────────────────────────────

/** What prompt assembly needs from a learned rule. */
export interface RuleLike {
  status: string;
  field: string | null;
  version: number | null;
  ruleText: string;
}

/** The rules that enter the prompt for one attribute: approved, for that field or for all. */
export function approvedRuleTexts(rules: RuleLike[], field: string): string[] {
  return rules
    .filter((r) => r.status === 'approved' && (r.field == null || r.field === field))
    .sort((a, b) => (a.version ?? 0) - (b.version ?? 0))
    .map((r) => r.ruleText);
}

// ── Gold set and regression gate (§2 I) ───────────────────────────────────────

export interface GoldCase {
  itemId: string;
  field: string;
  /** What a person accepted. */
  acceptedValue: string;
}

/**
 * Compare two sets of answers against what people accepted. A rule passes the
 * gate when it does not make the agent agree with people less often.
 */
export function regressionVerdict(
  gold: GoldCase[],
  without: Record<string, string | null>,
  withRule: Record<string, string | null>,
): { cases: number; matchesWithout: number; matchesWith: number; passes: boolean } {
  const key = (g: GoldCase) => `${g.itemId}|${g.field}`;
  let a = 0;
  let b = 0;
  for (const g of gold) {
    if ((without[key(g)] ?? '').toLowerCase() === g.acceptedValue.toLowerCase()) a++;
    if ((withRule[key(g)] ?? '').toLowerCase() === g.acceptedValue.toLowerCase()) b++;
  }
  return { cases: gold.length, matchesWithout: a, matchesWith: b, passes: b >= a };
}
